import { useState, useEffect } from "react";
import { 
  Alert, 
  Box, 
  Grid, 
  TextField, 
  Typography, 
  Button, 
  Paper, 
  Checkbox, 
  FormControlLabel, 
  Tooltip,
  Card,
  CardContent,
  Divider,
  Chip,
  Container,
  LinearProgress
} from '@mui/material';
import { Radio, RadioGroup, FormControl, FormLabel } from '@mui/material';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormGroup from "@mui/material/FormGroup";
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import successIcon from '../assets/payment-success-icon.png';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import EventIcon from '@mui/icons-material/Event';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../context/MemberProfileContext';
import parse from 'html-react-parser';

export default function HoliWalkthon() {
    // State management
    const [eventData, setEventData] = useState({
        "balaviharYear": "",
        "membershipType": { "name": "" },
        status: "",
        eventType: '',
        "notes": "first event1",
        "children": [],
        "medical": {
            "pcpName": "", "pcpMobile": "", "medicalInsurance": "",
            "groupNo": "", "idNo": "", "medicalNotes": "",
            "medicalConsentSignedBy": "", "treatmentConsentSignedBy": ""
        },
        transaction: {
            quantities: 0, itemPrice: 0, totalAmount: "",
            donationAmount: "", payerId: "", notes: "Multiple Events",
        },
        tshirts: [{ size: "", quantity: "0" }],
        "events": [],
        feeEnabled: 0,
        parent: { firstName: "", lastName: "", email: "", mobileNumber: "" }
    });

    // Hooks and state
    const { accessToken, signOut } = useAuth();
    const navigate = useNavigate();
    const { memberships, setMemberships } = useMember();
    const { memberProfile, eventTypes } = useMember();
    
    // Form state
    const [activeStep, setActiveStep] = useState(0);
    const [eventDetails, setEventDetails] = useState({});
    const [shirtFields, setShirtFields] = useState(eventData.tshirts);
    const [noOfShirts, setNoOfShirts] = useState(0);
    const [noOfPeople, setNoOfPeople] = useState(0);
    const [donation, setDonation] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [ticketPrice, setTicketPrice] = useState(0);
    const [tshirtPrice, setTshirtPrice] = useState(0);
    const [feeEnabled, setFeeEnabled] = useState(false);
    const [rulesAccepted, setRulesAccepted] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [checkout, setCheckout] = useState(false);
    const [loading, setLoading] = useState(false);

    const steps = ['Registration Details', 'Payment', 'Confirmation'];
    const API_URL = process.env.REACT_APP_API_URL;

    // Effects
    useEffect(() => {
        if (accessToken) {
            memberProfileFetch();
        } else {
            signOut();
            navigate('/');
        }
    }, [accessToken]);

    useEffect(() => {
        if (eventTypes && eventTypes.length > 0) {
            const parentEvent = eventTypes.find(et => et.name === 'TshirtHoliWalkthon');
            if (parentEvent) {
                setEventDetails(parentEvent);
                const childEvents = eventTypes.filter(et => et.parentEventTypeId === parentEvent.eventTypeId);
                
                const tshirtEvent = childEvents.find(et => et.name?.toLowerCase().includes('tshirt'));
                const holiEvent = eventTypes.find(et => et.name === 'HoliWalkthon');
                
                if (tshirtEvent) setTshirtPrice(Number(tshirtEvent.price) || 0);
                if (holiEvent) setTicketPrice(Number(holiEvent.price) || 0);
            }
        }
    }, [eventTypes]);

    useEffect(() => {
        const shirtsTotal = noOfShirts * tshirtPrice;
        const baseAmount = (noOfPeople * ticketPrice) + donation + shirtsTotal;
        const finalAmount = feeEnabled ? (baseAmount * 1.03) : baseAmount;
        setTotalAmount(finalAmount.toFixed(2));
    }, [noOfPeople, noOfShirts, donation, feeEnabled, ticketPrice, tshirtPrice]);

    // API Functions
    const memberProfileFetch = async () => {
        try {
            const response = await fetch(`${API_URL}/gquery`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-cmdfw-jwt-token': `${accessToken}`,
                },
                body: JSON.stringify({
                    query: `query { memberships { status balaviharPeriod currentMembership { membershipTypesEntity { name } } } }`
                }),
            });
            const data = await response.json();
            if (response.ok) {
                setMemberships(data.data.memberships);
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
        }
    };

    // Handlers
    const handleShirtSize = (e, index) => {
        const newForm = [...shirtFields];
        newForm[index][e.target.name] = e.target.value;
        setShirtFields(newForm);
    };

    const handleShirtQuantity = (e, index) => {
        const newForm = [...shirtFields];
        newForm[index][e.target.name] = e.target.value;
        setShirtFields(newForm);
        
        const total = newForm.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
        setNoOfShirts(total);
    };

    const handleAddShirtFields = () => {
        setShirtFields([...shirtFields, { size: "", quantity: "0" }]);
    };

    const handleRemoveShirtFields = (index) => {
        const values = [...shirtFields];
        values.splice(index, 1);
        setShirtFields(values);
        
        const total = values.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
        setNoOfShirts(total);
    };

    const handleNext = () => {
        if (activeStep === 0 && !rulesAccepted) {
            setError("Please accept the rules and policies to continue");
            return;
        }
        setError(null);
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleCancel = () => {
        navigate('/EventTypesForm');
    };

    const checkoutHandler = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            eventData.status = totalAmount > 0 ? "pending" : "paid";
            eventData.transaction.totalAmount = totalAmount;
            eventData.transaction.quantities = noOfPeople;
            eventData.transaction.donationAmount = donation;
            eventData.tshirts = shirtFields;
            eventData.feeEnabled = feeEnabled;

            if (totalAmount > 0) {
                setCheckout(true);
                setActiveStep(1);
            } else {
                await sendToServer();
                setSuccess(true);
                setActiveStep(2);
            }
        } catch (error) {
            setError("Registration failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const sendToServer = async () => {
        const response = await fetch(`${API_URL}/events`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                "x-cmdfw-jwt-token": `${accessToken}`,
            },
            body: JSON.stringify(eventData),
        });

        if (!response.ok) {
            throw new Error('Registration failed');
        }
        return response.json();
    };

    // PayPal Configuration
    const initialOptions = {
        clientId: process.env.REACT_APP_PROD_CLIENT_ID,
        currency: "USD",
        intent: "capture",
        "disable-funding": "paylater",
    };

    const createOrder = (data, actions) => {
        const itemArray = [];
        
        if (noOfPeople > 0 || noOfShirts > 0) {
            itemArray.push({
                name: "Holi Walkathon Registration",
                unit_amount: { currency_code: "USD", value: (noOfPeople * ticketPrice + noOfShirts * tshirtPrice).toFixed(2) },
                quantity: "1",
            });
        }
        
        if (donation > 0) {
            itemArray.push({
                name: "Donation",
                unit_amount: { currency_code: "USD", value: donation.toFixed(2) },
                quantity: "1",
            });
        }
        
        if (feeEnabled) {
            itemArray.push({
                name: "Processing Fee",
                unit_amount: { currency_code: "USD", value: ((noOfPeople * ticketPrice + noOfShirts * tshirtPrice + donation) * 0.03).toFixed(2) },
                quantity: "1",
            });
        }

        return actions.order.create({
            purchase_units: [{
                description: "Holi Walkathon Event Registration",
                amount: { value: totalAmount, currency_code: "USD" },
                items: itemArray,
            }],
        });
    };

    const onApprove = async (data, actions) => {
        const details = await actions.order.capture();
        eventData.transaction.paymentStatus = 'paid';
        eventData.status = "paid";
        eventData.transaction.payerId = details.payer.payer_id;
        
        await sendToServer();
        setSuccess(true);
        setActiveStep(2);
    };

    // Render Functions
    const renderRegistrationForm = () => (
        <Grid container spacing={4}>
            {/* Event Information Section */}
            <Grid item xs={12}>
                <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <EventIcon sx={{ color: '#667eea', mr: 2, fontSize: 28 }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                Event Information
                            </Typography>
                        </Box>
                        
                        <Box sx={{ 
                            p: 3, 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: 2, 
                            border: '1px solid #e9ecef',
                            mb: 3
                        }}>
                            <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#495057' }}>
                                {eventDetails.desc ? parse(eventDetails.desc) : "Loading event details..."}
                            </Typography>
                        </Box>

                        {memberships.status === "ACTIVE" ? (
                            <Box sx={{ 
                                p: 3, 
                                backgroundColor: '#d4edda', 
                                borderRadius: 2, 
                                border: '1px solid #c3e6cb',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <CheckCircleIcon sx={{ color: '#155724', mr: 2 }} />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#155724' }}>
                                        Membership Status: Active
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#155724' }}>
                                        Period: {memberships.balaviharPeriod} | Type: {memberships.currentMembership?.membershipTypesEntity?.name}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Membership expired.</strong> Please renew your membership for the period: {memberships.balaviharPeriod}
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* T-Shirt Section */}
            <Grid item xs={12}>
                <Card elevation={3} sx={{ 
                    border: '2px solid #e9ecef',
                    borderRadius: 4,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                        boxShadow: '0 12px 35px rgba(102, 126, 234, 0.15)',
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease',
                        borderColor: '#667eea'
                    }
                }}>
                    <Box sx={{ 
                        p: 1, 
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderBottom: '4px solid #667eea'
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#2c3e50',
                            letterSpacing: '0.5px',
                            mb: 1,
                            textAlign: 'left'
                        }}>
                            T-Shirt Orders
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: '#495057',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            textAlign: 'left'
                        }}>
                            Select sizes and quantities for event t-shirts
                        </Typography>
                    </Box>
                    <CardContent sx={{ p: 2, backgroundColor: '#fafbfc' }}>
                        {shirtFields.map((shirt, index) => (
                            <Box key={index} sx={{ 
                                mb: 1, 
                                p: 1, 
                                backgroundColor: '#ffffff',
                                borderRadius: 3,
                                border: '1px solid #e9ecef',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                            }}>
                                <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} sm={5}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel>Size</InputLabel>
                                            <Select
                                                name="size"
                                                value={shirt.size}
                                                onChange={(e) => handleShirtSize(e, index)}
                                                label="Size"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#ffffff',
                                                        '&:hover fieldset': { borderColor: '#667eea' },
                                                        '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                                    }
                                                }}
                                            >
                                                <MenuItem value="Adult-XL">Adult-XL</MenuItem>
                                                <MenuItem value="Adult-L">Adult-L</MenuItem>
                                                <MenuItem value="Adult-M">Adult-M</MenuItem>
                                                <MenuItem value="Adult-S">Adult-S</MenuItem>
                                                <MenuItem value="Youth-L">Youth-L</MenuItem>
                                                <MenuItem value="Youth-M">Youth-M</MenuItem>
                                                <MenuItem value="Youth-S">Youth-S</MenuItem>
                                                <MenuItem value="Youth-XS">Youth-XS</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Quantity"
                                            name="quantity"
                                            value={shirt.quantity}
                                            onChange={(e) => handleShirtQuantity(e, index)}
                                            inputProps={{ min: 0, max: 10 }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: '#ffffff',
                                                    '&:hover fieldset': { borderColor: '#667eea' },
                                                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            {index > 0 && (
                                                <IconButton 
                                                    onClick={() => handleRemoveShirtFields(index)}
                                                    sx={{ 
                                                        color: '#e74c3c',
                                                        backgroundColor: '#fff5f5',
                                                        '&:hover': { 
                                                            backgroundColor: '#fed7d7',
                                                            transform: 'scale(1.1)'
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                            <IconButton 
                                                onClick={handleAddShirtFields}
                                                sx={{ 
                                                    color: '#667eea',
                                                    backgroundColor: '#f0f4ff',
                                                    '&:hover': { 
                                                        backgroundColor: '#e0e7ff',
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                <PersonAddIcon />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            </Grid>

            {/* Event Tickets */}
            <Grid item xs={12}>
                <Card elevation={3} sx={{ 
                    border: '2px solid #e9ecef',
                    borderRadius: 4,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                        boxShadow: '0 12px 35px rgba(40, 167, 69, 0.15)',
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease',
                        borderColor: '#28a745'
                    }
                }}>
                    <Box sx={{ 
                        p: 1, 
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderBottom: '4px solid #28a745'
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#2c3e50',
                            letterSpacing: '0.5px',
                            mb: 1,
                            textAlign: 'left'
                        }}>
                            Event Tickets
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: '#495057',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            textAlign: 'left'
                        }}>
                            Number of participants for the Holi Walkathon
                        </Typography>
                    </Box>
                    <CardContent sx={{ p: 5, backgroundColor: '#fafbfc' }}>
                        <Box sx={{ 
                            p: 1, 
                            backgroundColor: '#ffffff',
                            borderRadius: 3,
                            border: '1px solid #e9ecef',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                        }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Number of Tickets"
                                value={noOfPeople}
                                onChange={(e) => setNoOfPeople(parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 20 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#ffffff',
                                        '&:hover fieldset': { borderColor: '#28a745' },
                                        '&.Mui-focused fieldset': { borderColor: '#28a745' }
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#28a745'
                                    }
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Payment Information */}
            <Grid item xs={12}>
                <Card elevation={3} sx={{ 
                    border: '2px solid #e9ecef',
                    borderRadius: 4,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                        boxShadow: '0 12px 35px rgba(253, 126, 20, 0.15)',
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease',
                        borderColor: '#fd7e14'
                    }
                }}>
                    <Box sx={{ 
                        p: 1, 
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderBottom: '4px solid #fd7e14'
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#2c3e50',
                            letterSpacing: '0.5px',
                            mb: 1,
                            textAlign: 'left'
                        }}>
                            Payment Information
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: '#495057',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            textAlign: 'left'
                        }}>
                            Registration fees and optional donations
                        </Typography>
                    </Box>
                    <CardContent sx={{ p: 5, backgroundColor: '#fafbfc' }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ 
                                    p: 1, 
                                    backgroundColor: '#ffffff',
                                    borderRadius: 3,
                                    border: '1px solid #e9ecef',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Additional Donation ($)"
                                        value={donation}
                                        onChange={(e) => setDonation(parseFloat(e.target.value) || 0)}
                                        inputProps={{ min: 0 }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#ffffff',
                                                '&:hover fieldset': { borderColor: '#fd7e14' },
                                                '&.Mui-focused fieldset': { borderColor: '#fd7e14' }
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#fd7e14'
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    p: 1, 
                                    backgroundColor: '#ffffff',
                                    borderRadius: 3,
                                    border: '1px solid #e9ecef',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={feeEnabled}
                                                onChange={(e) => setFeeEnabled(e.target.checked)}
                                                sx={{ color: '#fd7e14', '&.Mui-checked': { color: '#fd7e14' } }}
                                            />
                                        }
                                        label="Add 3% to cover credit card processing fees"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    p: 1, 
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: 4,
                                    color: 'white',
                                    textAlign: 'center',
                                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                                    border: '2px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        Total Amount: ${totalAmount}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {/* Terms & Conditions */}
            <Grid item xs={12}>
                <Card elevation={3} sx={{ 
                    border: '2px solid #e9ecef',
                    borderRadius: 4,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                        boxShadow: '0 12px 35px rgba(220, 53, 69, 0.15)',
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease',
                        borderColor: '#dc3545'
                    }
                }}>
                    <Box sx={{ 
                        p: 1, 
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderBottom: '4px solid #dc3545'
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: '#2c3e50',
                            letterSpacing: '0.5px',
                            mb: 1,
                            textAlign: 'left'
                        }}>
                            Terms & Conditions
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: '#495057',
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            textAlign: 'left'
                        }}>
                            Please review and accept the waiver agreement
                        </Typography>
                    </Box>
                    <CardContent sx={{ p: 5, backgroundColor: '#fafbfc' }}>
                        <Box sx={{ 
                            p: 1, 
                            backgroundColor: '#ffffff',
                            borderRadius: 3,
                            border: '1px solid #e9ecef',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                            mb: 3
                        }}>
                            <Typography variant="body1" sx={{ mb: 3, fontWeight: 600, color: '#2c3e50' }}>
                                Waiver Statement (Please read and agree before proceeding):
                            </Typography>
                            <Button
                                variant="outlined"
                                href="https://membership.cmdfw.org/docs/cmdfw-waiver.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    borderColor: '#dc3545',
                                    color: '#dc3545',
                                    borderRadius: 3,
                                    px: 1,
                                    py: 1.5,
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    '&:hover': { 
                                        borderColor: '#c82333', 
                                        backgroundColor: 'rgba(220, 53, 69, 0.04)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)'
                                    }
                                }}
                            >
                                📄 Open Waiver PDF
                            </Button>
                        </Box>
                        
                        <Box sx={{ 
                            p: 1, 
                            backgroundColor: '#ffffff',
                            borderRadius: 3,
                            border: '1px solid #e9ecef',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rulesAccepted}
                                        onChange={(e) => setRulesAccepted(e.target.checked)}
                                        sx={{ color: '#dc3545', '&.Mui-checked': { color: '#dc3545' } }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>
                                        I accept the rules and policies
                                    </Typography>
                                }
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderPayment = () => (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 600, color: '#2c3e50' }}>
                    Complete Your Payment
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, color: '#667eea' }}>
                    Total Amount: ${totalAmount}
                </Typography>
                
                <PayPalScriptProvider options={initialOptions}>
                    <PayPalButtons
                        style={{ layout: "vertical", label: "pay" }}
                        createOrder={createOrder}
                        onApprove={onApprove}
                    />
                </PayPalScriptProvider>
                
                <Button 
                    onClick={handleBack} 
                    variant="outlined" 
                    sx={{ mt: 3, borderColor: '#667eea', color: '#667eea' }}
                >
                    Back to Registration
                </Button>
            </CardContent>
        </Card>
    );

    const renderConfirmation = () => (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ mb: 4 }}>
                    <img src={successIcon} alt="Success" style={{ maxWidth: 120, height: 'auto' }} />
                </Box>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#28a745' }}>
                    Registration Successful!
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, color: '#2c3e50' }}>
                    Thank you for registering for the Holi Walkathon
                </Typography>
                <Box sx={{ 
                    p: 3, 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: 2, 
                    border: '1px solid #e9ecef',
                    mb: 4
                }}>
                    <Typography variant="body1" sx={{ color: '#495057' }}>
                        A confirmation email has been sent to: <strong>{memberProfile.primary?.email}</strong>
                    </Typography>
                </Box>
                <Button 
                    onClick={() => navigate('/EventTypesForm')} 
                    variant="contained"
                    size="large"
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        px: 4, py: 1.5
                    }}
                >
                    Return to Events
                </Button>
            </CardContent>
        </Card>
    );

    const getStepContent = (step) => {
        switch (step) {
            case 0: return renderRegistrationForm();
            case 1: return renderPayment();
            case 2: return renderConfirmation();
            default: return null;
        }
    };

    return (
        <Box sx={{ backgroundColor: '#f5f6fa', minHeight: '100vh', display: 'flex', justifyContent: 'center', py: 2 }}>
            <Container maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 2, width: '100%' }}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            fontWeight: 700,
                            color: '#2c3e50',
                            mb: 0.5,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                        }}
                    >
                        Holi Walkathon Registration
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6c757d', mb: 0.5 }}>
                        CMDFW Registration System
                    </Typography>
                    {loading && (
                        <Box sx={{ mt: 1, maxWidth: 400, mx: 'auto' }}>
                            <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#667eea' } }} />
                            <Typography variant="body2" sx={{ mt: 0.5, color: '#6c757d' }}>
                                Processing your registration...
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Step Content */}
                <Box sx={{ mb: 2, width: '100%', maxWidth: '900px' }}>
                    {getStepContent(activeStep)}
                </Box>

                {/* Navigation Buttons */}
                {activeStep < 2 && activeStep !== 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, width: '100%', maxWidth: '900px' }}>
                        <Button
                            onClick={activeStep === 0 ? handleCancel : handleBack}
                            variant="outlined"
                            size="medium"
                            sx={{ 
                                borderColor: '#6c757d', 
                                color: '#6c757d',
                                px: 3
                            }}
                        >
                            {activeStep === 0 ? 'Cancel' : 'Back'}
                        </Button>
                        
                        {activeStep === 0 ? (
                            <Button
                                onClick={checkoutHandler}
                                variant="contained"
                                size="medium"
                                disabled={!rulesAccepted || loading}
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    px: 3
                                }}
                            >
                                {totalAmount > 0 ? 'Proceed to Payment' : 'Complete Registration'}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                variant="contained"
                                size="medium"
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    px: 3
                                }}
                            >
                                Continue
                            </Button>
                        )}
                    </Box>
                )}

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: '900px' }}>
                        {error}
                    </Alert>
                )}

                {/* Contact Information */}
                <Box sx={{ textAlign: 'center', mt: 4, pt: 2, borderTop: '1px solid #e9ecef', width: '100%' }}>
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>
                        Questions? Contact us at{' '}
                        <a href="mailto:membership@cmdfw.org" style={{ color: '#667eea', textDecoration: 'none' }}>
                            membership@cmdfw.org
                        </a>
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}